import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import Button from '../ui/Button';
import { listDocumentTypes } from '../../api/workflow';
import { uploadLeadDocument } from '../../api/leads';
import { pushToast } from '../ui/toastBus';

export default function LeadDocumentUploadModal({ open, onClose, leadId, presetTypeCode }) {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes, enabled: open });

  const [file, setFile] = useState(null);
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && presetTypeCode && typesQuery.data) {
      const match = typesQuery.data.find((t) => t.code === presetTypeCode);
      if (match) setDocumentTypeId(String(match.id));
    }
  }, [open, presetTypeCode, typesQuery.data]);

  const mutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type_id', documentTypeId);
      if (expiryDate) formData.append('expiry_date', expiryDate);
      return uploadLeadDocument(leadId, formData);
    },
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Document uploaded.' });
      queryClient.invalidateQueries({ queryKey: ['leads', String(leadId)] });
      handleClose();
    },
    onError: (err) => setError(err.response?.data?.message || 'Could not upload document.'),
  });

  const handleClose = () => {
    setFile(null);
    setDocumentTypeId('');
    setExpiryDate('');
    setError('');
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file || !documentTypeId) {
      setError('Choose a file and document type.');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Upload document"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Upload
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select label="Document type" value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value)}>
          <option value="">Select…</option>
          {(typesQuery.data || []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">File (PDF, JPG, PNG — max 10MB)</label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files[0])}
            className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary-light"
          />
        </div>

        <DatePicker label="Expiry date (optional)" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />

        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
}
